/**
 * Marketing → Content: every episode of a series, every file it was rendered
 * to, and every word and link that goes with it. The platform pages
 * (MarketingInstagram) arrange the same content for one platform.
 * docs/FeatureDesigns/marketing/marketing-console-target.md
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader, TabStrip, type TabItem } from '@shared-ui/components';
import { marketingApi } from '../../api/marketingApi';
import { AssetTile, CopyField } from './MarketingParts';
import { FORMATS, FORMAT_LABELS, SERIES, cardC, episodesWithFiles, type Format } from './episodeContent';
import './Marketing.css';

export default function MarketingContent() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['marketing-assets', SERIES.id],
    queryFn: () => marketingApi.assets(SERIES.id),
    // The signed URLs last an hour; refetch well before they lapse.
    staleTime: 30 * 60 * 1000,
  });
  const [format, setFormat] = useState<Format>('reel');
  const episodes = useMemo(() => episodesWithFiles(data?.files ?? []), [data]);
  const tabs: TabItem<Format>[] = FORMATS.map(f => ({ key: f, label: FORMAT_LABELS[f] }));
  const shared = cardC(data?.files ?? [], format);

  return (
    <div className="mk-page">
      <PageHeader
        title="Content"
        subtitle={<>{SERIES.title} · {episodes.length} episodes · files from the <code>{data?.container ?? 'marketing'}</code> container</>}
      />
      {error && <p className="mk-error">Could not load the files: {(error as Error).message}</p>}
      {isLoading && <p className="mk-sub">Loading files…</p>}
      {data && data.files.length === 0 && (
        <p className="mk-sub">
          No files in this environment's <code>marketing</code> container yet. They are put there by
          {' '}<code>scripts/ops/syncMarketingAssets.ts --blob</code>.
        </p>
      )}

      <TabStrip tabs={tabs} active={format} onChange={setFormat} ariaLabel="Format" />

      {shared && (
        <section className="mk-shared">
          <h2>Shared by every episode</h2>
          <div className="mk-assets"><AssetTile asset={shared} label="Card C · all challenges" /></div>
        </section>
      )}

      {episodes.map(({ episode, files, links }) => {
        const inFormat = files.filter(f => f.format === format);
        return (
          <section key={episode.id} className="mk-episode">
            <header className="mk-episode-head">
              <span className="mk-num">#{episode.id}</span>
              <div>
                <h2>{episode.title}</h2>
                <span className="mk-sub">{episode.topic}</span>
              </div>
            </header>
            <div className="mk-episode-body">
              <div className="mk-assets">
                {inFormat.length === 0
                  ? <p className="mk-sub">No {FORMAT_LABELS[format]} files for this episode.</p>
                  : inFormat.map(f => <AssetTile key={f.path} asset={f} />)}
              </div>
              <div className="mk-words">
                <CopyField label="Post caption" text={episode.caption} />
                <CopyField label="Ad text · carousel" text={episode.adText} />
                <CopyField label="Ad text · reel" text={episode.reelAdText} />
                <CopyField label="Link · post" text={links.post} />
                <CopyField label="Link · ad" text={links.ad} />
                <CopyField label="Link · story sticker" text={links.story} />
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
